import { Link, NavLink, useNavigate } from "react-router-dom";
import { Coins, Diamond, LogOut, Menu, Shield, User, Wallet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { money, imgSrc } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const LINKS = [
  ["/discover", "Discover"], ["/messages", "Messages"], ["/cams", "Live Cams"],
  ["/market", "Exclusive"], ["/wallet", "Wallet"], ["/membership", "Membership"],
];

export const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
    <Diamond className="h-5 w-5 text-red-600" strokeWidth={1.5} />
    <span className="font-display text-2xl font-semibold tracking-wide">Diamond <span className="italic text-red-600">Luxury</span></span>
  </Link>
);

const NavItems = ({ onClick }) =>
  LINKS.map(([to, label]) => (
    <NavLink key={to} to={to} onClick={onClick} data-testid={`nav-link-${label.toLowerCase().replace(" ", "-")}`}
      className={({ isActive }) => `text-sm tracking-wide transition-colors hover:text-white ${isActive ? "text-white" : "text-zinc-400"}`}>
      {label}
    </NavLink>
  ));

function UserMenu({ user, logout }) {
  const nav = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="nav-user-menu" className="flex items-center gap-2 rounded-full border border-white/10 p-1 pr-3 hover:border-red-600/50">
        <img src={imgSrc(user.photo)} alt="" className="h-8 w-8 rounded-full object-cover" />
        <span className="text-sm">{user.name}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 border-zinc-800 bg-zinc-900">
        <DropdownMenuItem data-testid="menu-profile" onClick={() => nav("/me")}><User className="mr-2 h-4 w-4" />My profile</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-verify" onClick={() => nav("/verify")}><Shield className="mr-2 h-4 w-4" />ID verification</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-tokens" onClick={() => nav("/tokens")}><Coins className="mr-2 h-4 w-4" />Buy tokens</DropdownMenuItem>
        {user.role === "admin" && <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")}><Shield className="mr-2 h-4 w-4 text-red-500" />Admin panel</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }}><LogOut className="mr-2 h-4 w-4" />Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  return (
    <header className="sticky top-0 z-50 border-b border-white/5 bg-zinc-950/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
        <Logo />
        {user && <nav className="hidden items-center gap-7 lg:flex"><NavItems /></nav>}
        <div className="flex items-center gap-3">
          {user ? (
            <>
              <Link to="/tokens" data-testid="nav-token-balance" className="hidden items-center gap-1 text-sm text-amber-300 sm:flex"><Coins className="h-4 w-4" />{user.tokens}</Link>
              <Link to="/wallet" data-testid="nav-wallet-balance" className="hidden items-center gap-1 text-sm text-zinc-300 sm:flex"><Wallet className="h-4 w-4" />{money(user.wallet_cents)}</Link>
              <UserMenu user={user} logout={logout} />
              <Sheet>
                <SheetTrigger data-testid="nav-mobile-menu" className="lg:hidden"><Menu className="h-6 w-6" /></SheetTrigger>
                <SheetContent className="flex flex-col gap-6 border-zinc-800 bg-zinc-950 pt-16"><NavItems /></SheetContent>
              </Sheet>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-btn" className="text-sm text-zinc-300 hover:text-white">Log in</Link>
              <Link to="/register" data-testid="nav-join-btn" className="btn-red rounded-full px-5 py-2 text-sm font-semibold">Join now</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
